import { Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkLicenseIdDuplicate, updateLicense, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import { ILicense } from "../../../../types";
import { getApiErrorCode } from "../../../../utils";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage, underlineInputProps } from "../../../Auth/SignUp/StepLayout";

interface RejectedLicenseModalProps {
    isOpen: boolean;
    onClose: () => void;
    license: ILicense;
}

interface ILicenseForm {
    number: string;
}

const licenseIdRegex = /^\d{1,6}$/;

export default function RejectedLicenseModal({ isOpen, onClose, license }: RejectedLicenseModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [licenseImage, setLicenseImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedLicenseId, setDebouncedLicenseId] = useState("");

    const needsNumber = !license.numberIsValid;
    const needsImage = !license.imageIsValid;

    const { register, handleSubmit, watch, reset } = useForm<ILicenseForm>({
        defaultValues: { number: license.number },
    });
    const licenseId = watch("number") ?? "";
    const isLicenseIdFormatValid = licenseIdRegex.test(licenseId);
    const isSameAsCurrentNumber = licenseId === license.number;

    useEffect(() => {
        if (isOpen) {
            reset({ number: license.number });
            setLicenseImage(null);
            setErrorMessage("");
        }
    }, [isOpen, license, reset]);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedLicenseId(licenseId), 400);
        return () => clearTimeout(id);
    }, [licenseId]);

    const { isLoading: isCheckingLicenseId, isError: isLicenseIdCheckError, error: licenseIdCheckError } = useQuery({
        queryKey: ["checkLicenseIdDuplicate", debouncedLicenseId],
        queryFn: () => checkLicenseIdDuplicate({ licenseId: debouncedLicenseId }),
        enabled: isOpen && needsNumber && !!debouncedLicenseId && licenseIdRegex.test(debouncedLicenseId) && !isSameAsCurrentNumber,
        retry: false,
    });

    const isLicenseIdDuplicate = isLicenseIdCheckError && getApiErrorCode(licenseIdCheckError) === "LICENSE_ID_ALREADY_USED";
    const isLicenseIdCheckUnavailable = isLicenseIdCheckError && !isLicenseIdDuplicate;

    const checkLicenseIdDuplicateMutation = useMutation({
        mutationFn: checkLicenseIdDuplicate,
        retry: false,
    });
    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const updateLicenseMutation = useMutation({
        mutationFn: updateLicense,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "약사 면허가 다시 제출되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const licenseHint = (() => {
        if (!needsNumber || !licenseId) return null;
        if (!isLicenseIdFormatValid) {
            return { message: "약사 면허 번호 형식이 올바르지 않습니다.", tone: "error" as const };
        }
        if (isSameAsCurrentNumber) {
            return { message: "현재 등록된 면허 번호입니다. 올바른 번호로 수정해주세요.", tone: "muted" as const };
        }
        if (isCheckingLicenseId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isLicenseIdDuplicate) {
            return { message: "이미 사용 중인 약사 면허 번호입니다.", tone: "error" as const };
        }
        if (isLicenseIdCheckUnavailable) {
            return { message: "약사 면허 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 약사 면허 번호입니다.", tone: "success" as const };
    })();

    const onSubmit = async ({ number }: ILicenseForm) => {
        if (needsImage && !licenseImage) {
            setErrorMessage("약사 면허증 사진을 첨부해주세요.");
            return;
        }

        setErrorMessage("");

        try {
            const payload: { image?: string; number?: string } = {};

            if (needsNumber) {
                if (!isSameAsCurrentNumber) {
                    await checkLicenseIdDuplicateMutation.mutateAsync({ licenseId: number });
                }
                payload.number = number;
            }
            if (needsImage && licenseImage) {
                payload.image = await uploadFileMutation.mutateAsync({ file: licenseImage });
            }

            if (!payload.image && !payload.number) {
                setErrorMessage("수정할 면허 정보가 없습니다.");
                return;
            }

            await updateLicenseMutation.mutateAsync(payload);
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약사 면허 보완 중 문제가 발생했습니다."));
        }
    };

    const canSubmit =
        (needsNumber || needsImage) &&
        (!needsNumber || (isLicenseIdFormatValid && (!isLicenseIdCheckError || isSameAsCurrentNumber))) &&
        (!needsImage || Boolean(licenseImage)) &&
        !isCheckingLicenseId;

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>약사 면허 보완하기</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10} as="form" onSubmit={handleSubmit(onSubmit)}>
                    {license.rejectedReason && (
                        <Text fontSize={14} fontWeight={500} color={colors.apple} marginBottom={5}>
                            {license.rejectedReason}
                        </Text>
                    )}
                    <Text fontSize={12} fontWeight={500} color={colors.g3} marginBottom={5}>
                        유효하지 않은 항목만 다시 제출할 수 있습니다.
                    </Text>
                    {needsNumber && (
                        <FormControl>
                            <FormLabel fontSize="sm" color={colors.blue}>약사 면허 번호</FormLabel>
                            <Input
                                {...register("number", { required: true })}
                                type="text"
                                placeholder="약사 면허 번호"
                                {...underlineInputProps}
                            />
                            <FieldMessage message={licenseHint?.message} tone={licenseHint?.tone} />
                        </FormControl>
                    )}
                    {needsImage && (
                        <FormControl marginTop={needsNumber ? 8 : 0}>
                            <FormLabel fontSize="sm" color={colors.blue}>약사 면허증</FormLabel>
                            <FileAttach
                                label="01 | 약사 면허증"
                                inputId="rejected-license-upload"
                                file={licenseImage}
                                onChange={(file) => {
                                    setLicenseImage(file);
                                    setErrorMessage("");
                                }}
                            />
                        </FormControl>
                    )}
                    {!needsNumber && !needsImage && (
                        <Text fontSize={14} color={colors.g3}>보완할 항목이 없습니다.</Text>
                    )}
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            type="submit"
                            isLoading={
                                uploadFileMutation.isPending ||
                                updateLicenseMutation.isPending ||
                                checkLicenseIdDuplicateMutation.isPending
                            }
                            isDisabled={!canSubmit}
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            제출
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
